import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { isNull } from 'lodash';
// Import Components
import { Button, Icon, Tooltip } from '@geneui/components';
import { Notification } from 'components/Header/types';
// Import Hooks
import { usePartnerDateFormats } from 'hooks';
// Import Services
import { customMomentWithoutTimezoneConversion, parseNotificationContentMessage } from 'services/Helpers';
// Import SCSS
import styles from './notificationPanel.module.scss';
// Import Constants
import { l } from 'constants/common';

interface Props {
    notification: Notification;
    onClick: (notification: Notification) => void;
    onMarkAsRead: (notification: Notification) => void;
    onUnmarkAsRead: (notification: Notification) => void;
}
const NotificationItem = ({ notification, onClick, onMarkAsRead, onUnmarkAsRead }: Props) => {
    const { t } = useTranslation();
    const { partnerDateTimeWithoutSecondsFormat } = usePartnerDateFormats();
    const textRef = useRef<HTMLParagraphElement>(null);
    const [isTooltipActive, setIsTooltipActive] = useState(false);

    const { ListItem, CreatedDate, ReadDate } = notification;

    const formatDate = (date) => {
        return customMomentWithoutTimezoneConversion(date).format(partnerDateTimeWithoutSecondsFormat);
    };

    useEffect(() => {
        const el = textRef.current;

        if (el) {
            setIsTooltipActive(el.scrollWidth > el.clientWidth);
        }
    }, [notification]);

    const parsedContentMessage = useMemo(() => parseNotificationContentMessage(notification, t), [notification, t]);

    return (
        <div
            className={classNames(styles.notificationItemWrapper, { 'crm-cursor-pointer': !isNull(ListItem) })}
            onClick={() => !isNull(ListItem) && onClick(notification)}
        >
            <div className={styles.contentWrapper}>
                <h4>{ListItem?.Title}</h4>
                {isTooltipActive ? (
                    <Tooltip text={parsedContentMessage}>
                        <p ref={textRef}>{parsedContentMessage}</p>
                    </Tooltip>
                ) : (
                    <p ref={textRef}>{parsedContentMessage}</p>
                )}
                <span>{formatDate(CreatedDate)}</span>
            </div>
            <div className={styles.actionsWrapper}>
                <Tooltip text={t(ReadDate ? l.MarkAsUnread : l.MarkAsRead)}>
                    <Button
                        appearance="minimal"
                        color="primary"
                        icon={ReadDate ? 'bc-icon-crm-sms' : 'bc-icon-mail-template-48'}
                        onClick={(e) => {
                            e.stopPropagation();
                            ReadDate ? onUnmarkAsRead(notification) : onMarkAsRead(notification);
                        }}
                    />
                </Tooltip>
                {isNull(ReadDate) && <Icon type="bc-icon-dot" />}
            </div>
        </div>
    );
};

export default NotificationItem;
